import { BackButton, DataGridActionsColumn, TableView1 } from "..";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ConfirmNotification } from "../Dialogs";
import {
  useDeleteModule,
  useModulesList,
  useRolesList,
  useUpdateRoleModules,
} from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { Button, Stack } from "@mui/material";
import { AddModule } from "./AddModule";
import { useParams } from "react-router";
import { bool, string } from "prop-types";
import { enqueueSnackbar } from "notistack";

type ModulesListProps = {
  showAdd?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  parentPath?: string;
};

type ModuleRow = {
  id?: string;
  name?: string;
  code?: string;
  desc?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  [key: string]: unknown;
};

type ModuleAction = "module-edit" | "module-delete";

type DeleteMultipleIds = Array<string | number>;

type DialogParameters = {
  action?: string;
  code?: string;
  ids?: DeleteMultipleIds;
};

type ConfirmResultPayload = {
  result?: string;
  parameters?: DialogParameters;
};

type SelectionModel = Array<string | number>;

type ColumnValueGetterParams = {
  value?: string | number | Date | null;
};

type AddModuleHandleClose = boolean;

export const ModulesList = (props: ModulesListProps) => {
  const { key = "--none--" } = useParams();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const parentPath = props.parentPath ? `/${props.parentPath}` : "";
  const showAdd = props.showAdd ?? false;

  const { modulesList, loading: loadingList } = useModulesList({});
  const { deleteModule, deleting } = useDeleteModule();
  const { rolesList } = useRolesList({
    role: key,
  });

  const hasKey = useMemo(() => !!key && key !== "--none--", [key]);

  const { addOrUpdateRoleModule, loading: loadingUpdate } =
    useUpdateRoleModules(
      () => {
        // resetForm();
        // handleClose();
        enqueueSnackbar(gettext("The role has been updated"), {
          variant: "success",
        });
      },
      (error) => {
        if (error === "409") {
          // setEmailError(true);
        } else if (error === "410") {
          enqueueSnackbar(
            gettext("You are not authorized to perform this action"),
            {
              variant: "error",
            },
          );
        } else {
          enqueueSnackbar(gettext("Error: Something went wrong!"), {
            variant: "error",
          });
        }
      },
    );

  //region React Hook (useState)
  const [showConfirm, setShowConfirm] = useState(false);
  const [dialogParameters, setDialogParameters] = useState<DialogParameters>(
    {},
  );
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<ModuleRow>({});
  const [selectedIds, setSelectedIds] = useState<SelectionModel>(
    rolesList[0]?.modules || [],
  );
  const [roleName, setRoleName] = useState("");
  // const [disabledSaveButton, setDisabledSaveButton] = useState(true);
  //endregion React Hook (useState)

  //region React Hook (useEffect)
  useEffect(() => {
    const currentRole =
      rolesList.find((r) => r.key === key || r.id === key) || rolesList[0];
    if (currentRole && key) {
      const newModules = currentRole.modules || [];
      setSelectedIds((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(newModules)) {
          return newModules;
        }
        return prev;
      });

      const newRole = currentRole.role || "";
      setRoleName((prev) => {
        if (prev !== newRole) {
          return newRole;
        }
        return prev;
      });
    }
  }, [key, rolesList]);
  //endregion React Hook (useEffect)

  //#region useMemo

  const showItems = useMemo(() => {
    return {
      showEdit: props.showEdit ?? false,
      showDelete: props.showDelete ?? false,
    };
  }, [props.showEdit, props.showDelete]);

  const showActionsColumn = useMemo(() => {
    return Object.values(showItems).some((item) => item === true);
  }, [showItems]);

  //#endregion useMemo

  //#region Handlers

  const menuClick = (action: ModuleAction, row: ModuleRow) => {
    switch (action) {
      case "module-edit":
        handleEdit(row);
        break;
      case "module-delete":
        handleOnClickDeleteSingle(row);
        break;
      default:
        break;
    }
  };

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        code: !key,
        desc: true,
        createdAt: false,
        updatedAt: false,
        actions: !!props.showEdit || !!props.showDelete,
      },
    },
  };
  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
    },
    {
      field: "name",
      headerName: gettext("Module"),
      // flex: 1,
      minWidth: 250,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "code",
      headerName: gettext("Code"),
      // flex: 1,
      minWidth: 200,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "desc",
      headerName: gettext("Description"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "createdAt",
      headerName: gettext("Created At"),
      headerAlign: "left",
      type: "date",
      valueGetter: ({ value }: ColumnValueGetterParams) =>
        value && new Date(value),
    },
    {
      field: "updatedAt",
      headerName: gettext("Updated At"),
      headerAlign: "left",
      type: "date",
      valueGetter: ({ value }: ColumnValueGetterParams) =>
        value && new Date(value),
    },
    ...DataGridActionsColumn(showActionsColumn, "module", showItems, menuClick),
  ];

  //region Handlers (continued)

  const handleEdit = (row: ModuleRow) => {
    setEditRow(row);
    setAddFormOpen(true);
  };

  const handleOnClickDeleteSingle = (row: ModuleRow) => {
    setDialogParameters({
      action: "delete-single",
      code: row.code,
    });
    setShowConfirm(true);
  };

  const handleOnClickDeleteMultiple = (ids: DeleteMultipleIds) => {
    setDialogParameters({
      action: "delete-multi",
      ids: ids,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: ConfirmResultPayload) => {
    if (obj.result === "yes") {
      deleteModule({
        code: obj?.parameters?.code,
      });
    }
    setShowConfirm(false);
  };

  const SavePermissions = () => {
    const moduleCodes = Array.from(
      new Set(
        selectedIds.includes("allow-to-all")
          ? selectedIds
          : [...selectedIds, "allow-to-all"],
      ),
    );
    const variables = {
      moduleCodes,
      roleKey: key,
    };
    addOrUpdateRoleModule({
      variables: variables,
      // refetchQueries: ["getAllRoles"]
    }).then();
  };

  const handleOnRowSelectionModelChange = (ids: SelectionModel) => {
    setSelectedIds(ids);
  };

  const postToolbar = () => {
    return (
      <>
        <Stack direction="row" spacing={1} sx={{ pl: 1, alignItems: "center" }}>
          <BackButton link={`${parentPath}/roles`} />

          {showAdd && (
            <Button
              variant="contained"
              onClick={() => {
                setAddFormOpen(true);
              }}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {gettext("Add permission")}
            </Button>
          )}
          {hasKey && (
            <Button
              variant="contained"
              // disabled={disabledSaveButton}
              onClick={() => {
                SavePermissions();
              }}
            >
              {gettext("Save")}
            </Button>
          )}
        </Stack>
      </>
    );
  };

  const handleCloseAddForm = (event: AddModuleHandleClose) => {
    setAddFormOpen(event);
    setEditRow({});
  };

  return (
    <>
      <TableView1
        title={
          roleName
            ? gettext("Permissions for ") + roleName
            : gettext("Permissions")
        }
        rows={modulesList}
        // rows={[]}
        columns={columns}
        loading={loadingList || loadingUpdate}
        checkboxSelection={hasKey}
        initialState={initialState}
        // preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        // headerRight={headerRight()}
        showGridToolbar={true}
        showGridToolbarDeleteButton={false}
        showGridToolbarDensitySelector={false}
        showGridToolbarColumnsButton={false}
        showGridToolbarQuickFilter
        showGridToolbarFilterButton={false}
        showGridToolbarExport={false}
        onRowSelectionModelChange={handleOnRowSelectionModelChange}
        onClickDeleteMultiple={handleOnClickDeleteMultiple}
        selectedIds={selectedIds}
      />
      <AddModule
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
      />
      {deleting ? (
        <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">
          {/*<Spinner animation="border" variant="light"/>*/}
        </div>
      ) : null}
    </>
  );
};

ModulesList.propTypes = {
  showEdit: bool,
  showDelete: bool,
  parentPath: string,
};
